import React, { useState, useEffect, useCallback } from 'react';
import { getPayments, markPaymentPaid, generatePaymentCode } from '../lib/api';
import { Button } from '../components/ui/button';
import { Search, Waves, GraduationCap, Globe, RefreshCw, CheckCircle, Clock, CreditCard, ExternalLink, Copy, Hash, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`;

const LOCATION_LABELS = {
  san_antolin: 'San Antolín', san_martin: 'San Martín', ria_niembro: 'Ría de Niembro',
};
const METHOD_LABELS = {
  cash: 'Efectivo', bizum: 'Bizum', bizum_carlos: 'Bizum Carlos',
  bizum_javier: 'Bizum Javier', bizum_alfredo: 'Bizum Alfredo',
  caixa: 'Caixa', web_demo: 'Web', web: 'Web',
};

function DateRange({ payment }) {
  const { date, date_to, num_days } = payment;
  if (!date) return <span className="text-[#8A9BA8]">—</span>;
  if (num_days > 1 && date_to) {
    return (
      <div>
        <div className="text-sm text-[#4A5D6C]">{fmtDate(date)}</div>
        <div className="text-xs text-[#8A9BA8]">→ {fmtDate(date_to)} · {num_days}d</div>
      </div>
    );
  }
  return <span className="text-sm text-[#4A5D6C]">{fmtDate(date)}</span>;
}

function StatusBadge({ isPaid }) {
  if (isPaid) return (
    <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
      <CheckCircle className="w-3 h-3" /> Pagado
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
      <Clock className="w-3 h-3" /> Pendiente
    </span>
  );
}

function CodeCell({ payment, onGenerate }) {
  const [loading, setLoading] = useState(false);
  const code = payment.payment_code;
  const isPaid = parseFloat(payment.amount_pending || 0) <= 0 && parseFloat(payment.amount_paid || 0) > 0;

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado');
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await onGenerate(payment.id);
    } finally {
      setLoading(false);
    }
  };

  if (code) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-xs font-bold tracking-wider text-[#4A6D7C] bg-[#4A6D7C]/10 px-2 py-0.5 rounded">
          {code}
        </span>
        <button onClick={handleCopy} className="text-gray-400 hover:text-[#4A6D7C] transition-colors" title="Copiar código">
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  if (isPaid) return <span className="text-xs text-gray-300">—</span>;

  return (
    <button onClick={handleGenerate} disabled={loading}
      className="flex items-center gap-1 text-xs border border-dashed border-[#4A6D7C] text-[#4A6D7C] px-2 py-1 rounded-lg hover:bg-[#4A6D7C]/10 transition-colors disabled:opacity-50">
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Hash className="w-3 h-3" />}
      Generar
    </button>
  );
}

export default function PaymentsAdminPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, source: sourceFilter };
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await getPayments(params);
      setPayments(data);
    } catch {
      toast.error('Error cargando pagos');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, sourceFilter, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(fetchPayments, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchPayments]);

  const handleMarkPaid = async (id, name) => {
    try {
      await markPaymentPaid(id);
      toast.success(`${name} marcado como pagado`);
      fetchPayments();
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const handleGenerateCode = async (id) => {
    try {
      const { data } = await generatePaymentCode(id);
      toast.success(`Código generado: ${data.code}`);
      fetchPayments();
    } catch {
      toast.error('Error generando código');
    }
  };

  const totalCobrado = payments.reduce((s, p) => s + parseFloat(p.amount_paid || 0), 0);
  const totalPendiente = payments.reduce((s, p) => s + parseFloat(p.amount_pending || 0), 0);
  const totalWeb = payments.filter(p => p.source === 'web').reduce((s, p) => s + parseFloat(p.amount_paid || 0), 0);
  const pendingCount = payments.filter(p => parseFloat(p.amount_pending || 0) > 0).length;

  return (
    <div className="pt-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2B3C]">Pagos</h1>
          <p className="text-sm text-[#8A9BA8] mt-0.5">Una fila por reserva · genera códigos para cobro online</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/pay" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#4A6D7C] text-[#4A6D7C] rounded-lg hover:bg-[#4A6D7C] hover:text-white transition-colors">
            <ExternalLink className="w-3 h-3" /> Página de pago
          </a>
          <Button size="sm" variant="outline" onClick={fetchPayments}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-4">
          <div className="text-xs text-[#8A9BA8] mb-1">Total cobrado</div>
          <div className="text-xl font-bold text-[#1A2B3C]">{fmtEur(totalCobrado)}</div>
          <div className="text-xs text-gray-400 mt-0.5">{payments.filter(p => parseFloat(p.amount_paid||0) > 0).length} reservas</div>
        </div>
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-4">
          <div className="text-xs text-[#8A9BA8] mb-1">Pendiente de cobro</div>
          <div className="text-xl font-bold text-red-500">{fmtEur(totalPendiente)}</div>
          {pendingCount > 0 && <div className="text-xs text-red-400 mt-0.5">{pendingCount} reserva{pendingCount > 1 ? 's' : ''}</div>}
        </div>
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-4">
          <div className="text-xs text-[#8A9BA8] mb-1">Cobrado por web</div>
          <div className="text-xl font-bold text-teal-600">{fmtEur(totalWeb)}</div>
          <div className="text-xs text-gray-400 mt-0.5">{payments.filter(p => p.source === 'web').length} operaciones</div>
        </div>
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-4">
          <div className="text-xs text-[#8A9BA8] mb-1">Total reservas</div>
          <div className="text-xl font-bold text-[#1A2B3C]">{payments.length}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E0D8] rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]"
              placeholder="Buscar por nombre…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            {[['all', 'Todos'], ['paid', 'Pagados'], ['pending', 'Pendientes']].map(([val, lbl]) => (
              <button key={val} onClick={() => setStatusFilter(val)}
                className={`px-3 py-2 font-medium transition-colors ${statusFilter === val ? 'bg-[#4A6D7C] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {lbl}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            {[['all', 'Todos'], ['web', 'Web'], ['manual', 'Manual']].map(([val, lbl]) => (
              <button key={val} onClick={() => setSourceFilter(val)}
                className={`px-3 py-2 font-medium transition-colors ${sourceFilter === val ? 'bg-[#4A6D7C] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                {lbl}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]" />
            <span className="text-gray-400 text-xs">—</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#4A6D7C]" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-gray-400 hover:text-red-500">✕</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#8A9BA8]">Cargando…</div>
      ) : payments.length === 0 ? (
        <div className="text-center py-12 text-[#8A9BA8]">
          <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>No hay pagos con los filtros actuales</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E0D8] rounded-xl overflow-hidden">
          <div className="grid grid-cols-[minmax(150px,2fr)_100px_62px_82px_82px_90px_148px_96px] gap-x-2 px-4 py-2.5 bg-[#F5F2ED] border-b border-[#E5E0D8] text-xs font-semibold text-[#8A9BA8] uppercase tracking-wide">
            <div>Nombre</div>
            <div>Fechas</div>
            <div>Tipo</div>
            <div className="text-right">Total</div>
            <div className="text-right">Pendiente</div>
            <div>Método</div>
            <div>Código cobro</div>
            <div></div>
          </div>

          <div className="divide-y divide-[#E5E0D8]">
            {payments.map(p => {
              const pending = parseFloat(p.amount_pending || 0);
              const paid = parseFloat(p.amount_paid || 0);
              const total = parseFloat(p.income_total || 0);
              const isPaid = pending <= 0 && paid > 0;
              const isWeb = p.source === 'web';
              const isMulti = (p.num_days || 1) > 1;

              return (
                <div key={p.id}
                  className={`grid grid-cols-[minmax(150px,2fr)_100px_62px_82px_82px_90px_148px_96px] gap-x-2 px-4 py-3 items-center hover:bg-[#F5F2ED]/50 transition-colors
                    ${isWeb ? 'border-l-2 border-l-teal-400' : 'border-l-2 border-l-transparent'}`}>

                  <div className="flex items-start gap-2 min-w-0 pr-2">
                    <div className="mt-0.5 flex-shrink-0">
                      {p.source_type === 'course' ? <GraduationCap className="w-4 h-4 text-[#4A6D7C]" /> : <Waves className="w-4 h-4 text-teal-500" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-[#1A2B3C] truncate leading-tight">{p.display_name}</div>
                      <div className="text-xs text-[#8A9BA8] truncate mt-0.5">
                        {LOCATION_LABELS[p.location] || p.location || '—'}
                        {isWeb && <span className="ml-1 text-teal-600 font-medium"><Globe className="w-3 h-3 inline mb-0.5" /></span>}
                      </div>
                    </div>
                  </div>

                  <div><DateRange payment={p} /></div>

                  <div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.source_type === 'course' ? 'bg-blue-50 text-blue-700' : 'bg-cyan-50 text-cyan-700'}`}>
                      {p.source_type === 'course' ? 'Curso' : 'Alquiler'}
                    </span>
                  </div>

                  <div className="text-sm font-semibold text-[#1A2B3C] text-right">
                    {fmtEur(total)}
                    {isMulti && <div className="text-xs text-gray-400 font-normal">{p.num_days}d</div>}
                  </div>

                  <div className={`text-sm font-semibold text-right ${pending > 0 ? 'text-red-500' : 'text-gray-300'}`}>
                    {pending > 0 ? fmtEur(pending) : '—'}
                  </div>

                  <div className="text-xs text-[#4A5D6C] truncate">
                    {METHOD_LABELS[p.payment_method] || p.payment_method || '—'}
                  </div>

                  <div>
                    <CodeCell payment={p} onGenerate={handleGenerateCode} />
                  </div>

                  <div className="flex flex-col items-start gap-1.5 pr-2">
                    <StatusBadge isPaid={isPaid} />
                    {!isPaid && (
                      <button onClick={() => handleMarkPaid(p.id, p.display_name)}
                        className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-lg hover:bg-green-100 transition-colors whitespace-nowrap flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Cobrado
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
